import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";

const fmtDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-AR");
};

const toInt = (value) => Number(value || 0);

function criticalCount(instrument) {
  return (
    toInt(instrument.service_orders_count) +
    toInt(instrument.remito_items_count) +
    toInt(instrument.calibrations_count) +
    toInt(instrument.pressure_sessions_count)
  );
}

function softCount(instrument) {
  return (
    toInt(instrument.intake_items_count) +
    toInt(instrument.po_instruments_count) +
    toInt(instrument.offer_services_count) +
    toInt(instrument.files_count)
  );
}

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-600/70 bg-slate-700/30 text-slate-100",
    emerald: "border-emerald-500/60 bg-emerald-500/10 text-emerald-100",
    amber: "border-amber-500/60 bg-amber-500/10 text-amber-100",
    red: "border-red-500/60 bg-red-500/10 text-red-100",
    sky: "border-sky-500/60 bg-sky-500/10 text-sky-100",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tones[tone]}`}>
      {children}
    </span>
  );
}

export default function InstrumentDuplicatesAdminPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedMasters, setSelectedMasters] = useState({});
  const [resolvingKey, setResolvingKey] = useState("");
  const [notice, setNotice] = useState("");

  async function loadGroups() {
    setLoading(true);
    setError("");
    setNotice("");

    const { data, error: rpcError } = await supabase.rpc(
      "get_instrument_duplicate_groups"
    );

    if (rpcError) {
      setError(rpcError.message || "No se pudieron cargar los duplicados.");
      setLoading(false);
      return;
    }

    const rows = data || [];
    setGroups(rows);
    setSelectedMasters((prev) => {
      const next = {};
      rows.forEach((group, index) => {
        const key = groupKey(group, index);
        next[key] = prev[key] || group.suggested_master_id || group.instruments?.[0]?.id;
      });
      return next;
    });
    setLoading(false);
  }

  useEffect(() => {
    loadGroups();
  }, []);

  const filteredGroups = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return groups;

    return groups.filter((group) => {
      const haystack = [
        group.client_number,
        group.client_name,
        group.serial_key,
        group.brand_key,
        group.model_key,
        ...(group.instruments || []).flatMap((instrument) => [
          instrument.lab_id,
          instrument.tag,
          instrument.serial,
          instrument.brand,
          instrument.model,
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [groups, query]);

  const totals = useMemo(() => {
    return groups.reduce(
      (acc, group) => {
        acc.groups += 1;
        acc.instruments += Number(group.duplicate_count || 0);
        acc.potential += Number(group.potential_duplicates || 0);
        return acc;
      },
      { groups: 0, instruments: 0, potential: 0 }
    );
  }, [groups]);

  async function resolveGroup(group, index) {
    const key = groupKey(group, index);
    const masterId = selectedMasters[key];
    const instruments = group.instruments || [];
    const master = instruments.find((item) => item.id === masterId);

    if (!master) {
      alert("Elegi el ID maestro antes de resolver.");
      return;
    }

    const duplicates = instruments.filter((item) => item.id !== masterId);
    const blocked = duplicates.filter((item) => criticalCount(item) > 0);

    if (blocked.length > 0) {
      alert(
        "Hay IDs a eliminar con OS, remito o calibracion. Elegi como maestro el ID con referencias fuertes, o revisalo manualmente."
      );
      return;
    }

    const ok = window.confirm(
      [
        `Se conservara el Lab ID ${master.lab_id}.`,
        `Se eliminaran ${duplicates.length} duplicado(s) sin referencias criticas.`,
        "Las referencias blandas como precios, intake, oferta y archivos se moveran al maestro.",
        "",
        "Continuar?",
      ].join("\n")
    );
    if (!ok) return;

    setResolvingKey(key);
    setNotice("");
    setError("");

    try {
      for (const duplicate of duplicates) {
        const { error: rpcError } = await supabase.rpc(
          "resolve_instrument_duplicate",
          {
            p_master_instrument_id: masterId,
            p_duplicate_instrument_id: duplicate.id,
          }
        );

        if (rpcError) throw rpcError;
      }

      setNotice(`Duplicados resueltos. Se conservo el Lab ID ${master.lab_id}.`);
      await loadGroups();
    } catch (err) {
      setError(err.message || "No se pudo resolver el duplicado.");
    } finally {
      setResolvingKey("");
    }
  }

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-amber-300">
              Admin
            </p>
            <h1 className="text-3xl font-bold">Duplicados de instrumentos</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-300">
              Revisa instrumentos repetidos por cliente, serie, marca y modelo.
              El sistema sugiere conservar el ID con OS, remito, calibracion o
              precio asociado.
            </p>
          </div>

          <button
            type="button"
            onClick={loadGroups}
            disabled={loading || resolvingKey}
            className="rounded-md border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60"
          >
            Actualizar
          </button>
        </div>

        <section className="mb-5 grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4">
            <div className="text-sm text-slate-400">Grupos duplicados</div>
            <div className="mt-1 text-2xl font-bold">{totals.groups}</div>
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4">
            <div className="text-sm text-slate-400">Instrumentos involucrados</div>
            <div className="mt-1 text-2xl font-bold">{totals.instruments}</div>
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4">
            <div className="text-sm text-slate-400">Duplicados potenciales</div>
            <div className="mt-1 text-2xl font-bold">{totals.potential}</div>
          </div>
        </section>

        <div className="mb-5 rounded-lg border border-slate-800 bg-slate-900/70 p-4">
          <label className="text-sm font-semibold text-slate-200" htmlFor="duplicate-search">
            Buscar por cliente, Lab ID, serie, tag, marca o modelo
          </label>
          <input
            id="duplicate-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="mt-2 w-full rounded-md border border-slate-700 bg-white px-3 py-2 text-slate-950 outline-none focus:border-amber-400"
            placeholder="Ej: SUREN, 6339, 00213460, MSA"
          />
        </div>

        {notice && (
          <div className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            {notice}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-6 text-slate-300">
            Cargando duplicados...
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-6 text-slate-300">
            No hay duplicados para ese filtro.
          </div>
        ) : (
          <div className="space-y-4">
            {filteredGroups.map((group, index) => {
              const key = groupKey(group, index);
              const selectedMaster = selectedMasters[key] || group.suggested_master_id;

              return (
                <section
                  key={key}
                  className="rounded-lg border border-slate-800 bg-slate-900/70"
                >
                  <div className="flex flex-col gap-3 border-b border-slate-800 p-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-bold">
                          {group.client_number ? `${group.client_number} - ` : ""}
                          {group.client_name || "Sin cliente"}
                        </h2>
                        <Badge tone="amber">
                          {group.potential_duplicates} duplicado(s)
                        </Badge>
                      </div>
                      <div className="mt-1 text-sm text-slate-300">
                        Serie {group.serial_key || "-"} · Marca {group.brand_key || "-"} ·
                        Modelo {group.model_key || "-"}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => resolveGroup(group, index)}
                      disabled={resolvingKey === key}
                      className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-500 disabled:opacity-60"
                    >
                      {resolvingKey === key ? "Resolviendo..." : "Resolver duplicado"}
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-slate-950/60 text-xs uppercase text-slate-400">
                        <tr>
                          <th className="px-4 py-3">Conservar</th>
                          <th className="px-4 py-3">Lab ID</th>
                          <th className="px-4 py-3">Datos</th>
                          <th className="px-4 py-3">Referencias fuertes</th>
                          <th className="px-4 py-3">Otras refs.</th>
                          <th className="px-4 py-3">Origen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(group.instruments || []).map((instrument) => {
                          const isMaster = selectedMaster === instrument.id;
                          const isSuggested = group.suggested_master_id === instrument.id;
                          const strong = criticalCount(instrument);
                          const soft = softCount(instrument);

                          return (
                            <tr
                              key={instrument.id}
                              className={isMaster ? "bg-emerald-500/5" : "border-t border-slate-800"}
                            >
                              <td className="px-4 py-3 align-top">
                                <label className="inline-flex items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`master-${key}`}
                                    checked={isMaster}
                                    onChange={() =>
                                      setSelectedMasters((prev) => ({
                                        ...prev,
                                        [key]: instrument.id,
                                      }))
                                    }
                                  />
                                  <span>{isMaster ? "Maestro" : "Elegir"}</span>
                                </label>
                                {isSuggested && (
                                  <div className="mt-2">
                                    <Badge tone="emerald">Sugerido</Badge>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 align-top">
                                <div className="text-lg font-bold text-white">
                                  #{instrument.lab_id || "-"}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {fmtDate(instrument.created_at)}
                                </div>
                              </td>
                              <td className="px-4 py-3 align-top">
                                <div className="font-semibold">
                                  {instrument.tag || "Sin tag"}
                                </div>
                                <div className="text-slate-300">
                                  Serie {instrument.serial || "-"}
                                </div>
                                <div className="text-slate-400">
                                  {[instrument.brand, instrument.model].filter(Boolean).join(" ") ||
                                    "-"}
                                </div>
                              </td>
                              <td className="px-4 py-3 align-top">
                                <div className="flex flex-wrap gap-1">
                                  <Badge tone={instrument.service_orders_count ? "sky" : "slate"}>
                                    OS {instrument.service_orders_count || 0}
                                  </Badge>
                                  <Badge tone={instrument.remito_items_count ? "sky" : "slate"}>
                                    Remitos {instrument.remito_items_count || 0}
                                  </Badge>
                                  <Badge tone={instrument.service_prices_count ? "sky" : "slate"}>
                                    Precios {instrument.service_prices_count || 0}
                                  </Badge>
                                  <Badge
                                    tone={
                                      instrument.calibrations_count ||
                                      instrument.pressure_sessions_count
                                        ? "sky"
                                        : "slate"
                                    }
                                  >
                                    Calib.{" "}
                                    {toInt(instrument.calibrations_count) +
                                      toInt(instrument.pressure_sessions_count)}
                                  </Badge>
                                </div>
                                {strong > 0 && (
                                  <div className="mt-2 text-xs text-amber-200">
                                    Protegido: no se elimina si no es maestro.
                                  </div>
                                )}
                                {strong === 0 && toInt(instrument.service_prices_count) > 0 && (
                                  <div className="mt-2 text-xs text-slate-300">
                                    Precio solo: se mueve al maestro al resolver.
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 align-top">
                                <div className="flex flex-wrap gap-1">
                                  <Badge tone={soft ? "amber" : "slate"}>
                                    Intake {instrument.intake_items_count || 0}
                                  </Badge>
                                  <Badge tone={soft ? "amber" : "slate"}>
                                    OC {instrument.po_instruments_count || 0}
                                  </Badge>
                                  <Badge tone={soft ? "amber" : "slate"}>
                                    Ofertas {instrument.offer_services_count || 0}
                                  </Badge>
                                  <Badge tone={soft ? "amber" : "slate"}>
                                    Archivos {instrument.files_count || 0}
                                  </Badge>
                                </div>
                              </td>
                              <td className="px-4 py-3 align-top">
                                {instrument.import_batch ? (
                                  <Badge tone="amber">Importado</Badge>
                                ) : (
                                  <Badge tone="slate">App</Badge>
                                )}
                                <div className="mt-2 text-xs text-slate-400">
                                  {instrument.status || "-"}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}

function groupKey(group) {
  return [
    group.client_id || "no-client",
    group.serial_key || "no-serial",
    group.brand_key || "no-brand",
    group.model_key || "no-model",
  ].join("|");
}
